import React, { forwardRef, useCallback, useEffect } from "react";

import { EMPTY_ARRAY } from "@multiweb/reactUtils";
import { ForwardRefType } from "@multiweb/types/react";
import { RichTextEditorWrapper } from "UI/UI.React/richtextEditorWrapper/components/RichTextEditorWrapper";
import { ControlValueWrapper } from "../ControlValueWrapper";
import { FormComponentPropsWrapper } from "../FormComponentPropsWrapper";
import { InputFake } from "../InputFake";
import { MultiEditWrapper, MultiEditWrapperFakeComponentRendererProps } from "../MultiEditWrapper";
import { Skeleton } from "../Skeleton";
import { LoadableEditableControlComponentProps } from "../types";
import { ValidationErrorWrapper } from "../ValidationErrorWrapper";

/** API klic, pro self-hosted knihovny je v podstate k nicemu, ale tinyMCE bez nej v nekterych mistech padal */
const API_KEY = "7lrux57ppwu9ztm451g9yb41idv9z4olsfctww3d48famduq";
const PLUGINS = "print preview paste importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media template codesample table charmap hr pagebreak nonbreaking anchor toc insertdatetime advlist lists wordcount textpattern noneditable help charmap quickbars emoticons";
const TOOLBAR = "undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor backcolor removeformat | pagebreak | charmap emoticons | fullscreen  preview save print | insertfile image media template link anchor codesample | ltr rtl";

/**
 * @property {string | undefined} toolbar - toolbar funkce v tinyMCE
 * @property {string | undefined} plugins - extensions pro funkce v toolbaru, pokud nějaké mají
 * @property {string | undefined} language - jazyk názvů a popisků
 * @property {boolean | undefined} showMenuBar - zobrazení horní lišty (Soubor, Úpravy, ...)
 */
export type RichTextEditProps = {
    toolbar?: string;
    plugins?: string;
    language?: string;
    showMenuBar?: boolean;
} & Omit<LoadableEditableControlComponentProps, "ref">;

export const RichTextEdit: ForwardRefType<HTMLTextAreaElement, RichTextEditProps> = forwardRef<HTMLTextAreaElement, RichTextEditProps>(
    function RichTextEdit({
        id,
        dataKey,
        externalErrors,
        isInRequiredInvalidState,
        onValidationCallback,
        onValidityChanged,
        onBlur,
        onChange: onChangeExternal,
        onMount,
        onKeyDown,
        onClick,
        triggerValueChange,
        locKey,
        multiEditActive,
        touched,
        enabledVisual,
        recommended,
        required,
        postponedRequired,
        isEditing,
        isLoading,
        toolbar,
        plugins,
        language,
        showMenuBar,
        // Reference je zde kvůli EditableControlConnectoru.
        // @ts-ignore
        // eslint-disable-next-line unused-imports/no-unused-vars
    }, ref) {
        useEffect(() => {
            onMount?.({
                getDisplayedValue: (rawValue) => rawValue,
                getControlValue: (rawValue) => rawValue,
                convertControlValueToRawValue: (controlValue) => controlValue,
            });
        }, [onMount]);

        const fakeComponentRenderer = useCallback(({ disabled }: MultiEditWrapperFakeComponentRendererProps) => (
            <InputFake { ...{ disabled, isEditing, locKey, onKeyDown, onClick, required, recommended, postponedRequired } } />
        ), [isEditing, locKey, onKeyDown, onClick, required, recommended, postponedRequired]);

        return (
            <ControlValueWrapper dataKey={dataKey}>
                {({ rawValue, onChange }) => (
                    <ValidationErrorWrapper
                        {...{ dataKey, externalErrors, isInRequiredInvalidState, onValidityChanged }}
                        instantValidationFnDescriptions={EMPTY_ARRAY}
                        explicitValidationFnDescriptions={EMPTY_ARRAY}
                    >
                        {({ invalid, validate }) => (
                            <FormComponentPropsWrapper {...{ postponedRequired, required, recommended, enabledVisual }}>
                                {({ disabled, readOnly }) => (
                                    <MultiEditWrapper fakeComponentRenderer={fakeComponentRenderer} {...{ multiEditActive, disabled, isEditing, touched }}>
                                        {() => {
                                            if (isLoading) {
                                                return (<Skeleton />);
                                            }
                                            return (
                                                <RichTextEditorWrapper
                                                    { ... { id, dataKey }}
                                                    isValid={invalid}
                                                    apiKey={API_KEY}
                                                    disabled={disabled || readOnly}
                                                    scriptLoading={{ async: true, defer: true }}
                                                    onBlur={(e) => {
                                                        validate(onValidationCallback);
                                                        onBlur?.(e as any);
                                                        triggerValueChange?.();
                                                    }}
                                                    onEditorChange={
                                                        (_e, editor) => {
                                                            onChange(editor.getContent());
                                                            onChangeExternal?.(editor.getContent());
                                                        }
                                                    }
                                                    value={rawValue}
                                                    init={{
                                                        toolbar_mode: "sliding",
                                                        height: "100%",
                                                        width: "100%",
                                                        menubar: showMenuBar ?? true,
                                                        plugins: plugins ?? PLUGINS,
                                                        toolbar: toolbar ?? TOOLBAR,
                                                        language: language,
                                                        resize: false,
                                                        image_advtab: true,
                                                        autosave_ask_before_unload: false,
                                                        paste_data_images: true,
                                                        promotion: false,
                                                        license_key: "gpl",
                                                        entity_encoding:"raw",
                                                    }}
                                                />
                                            );
                                        }
                                        }
                                    </MultiEditWrapper>
                                )}
                            </FormComponentPropsWrapper>
                        )}
                    </ValidationErrorWrapper>
                )}
            </ControlValueWrapper>
        );
    },
);
